import type { CSSProperties, ElementType } from 'react';
import { clsx } from 'clsx';
import type { Space } from '../../styles/tokens';
import { buildResponsiveProps, type ResponsiveValue } from '../../util/responsive';
import { sanitizeCSSVariables } from '../../util/style';
import type { Polymorphic } from '../polymorphism/polymorphic';
import styles from './horizontal-stack.module.scss';

export type HorizontalStackAlign = 'start' | 'center' | 'end' | 'space-around' | 'space-between' | 'space-evenly';

export type HorizontalStackBlockAlign = 'start' | 'center' | 'end' | 'baseline' | 'stretch';

const DEFAULT_HORIZONTAL_STACK_ELEMENT = 'div';

export type DefaultHorizontalStackElement = typeof DEFAULT_HORIZONTAL_STACK_ELEMENT;

export type HorizontalStackWrap = CSSProperties['flexWrap'];

const COMPONENT_STYLE_NAME = 'horizontal-stack';

interface BaseHorizontalStackProps {
  // align controls the horizontal alignment of children
  align?: HorizontalStackAlign;
  // blockAlign controls the vertical alignment of children
  blockAlign?: HorizontalStackBlockAlign;
  fullWidth?: boolean;
  // gap is the spacing between children
  gap?: ResponsiveValue<Space>;
  // rowGap is the spacing between children when they wrap to additional rows
  rowGap?: ResponsiveValue<Space>;
  // reversed controls if the children render in their provided order or reversed
  reversed?: boolean;
  // wrap controls if the children will render in additional rows when there's not enough screen space
  wrap?: ResponsiveValue<HorizontalStackWrap>;
}

export type HorizontalStackProps<AsComponent extends ElementType = DefaultHorizontalStackElement> = Polymorphic.Props<
  AsComponent,
  BaseHorizontalStackProps
>;

export function HorizontalStack<AsComponent extends ElementType = DefaultHorizontalStackElement>({
  align,
  as,
  blockAlign,
  children,
  className,
  fullWidth,
  gap,
  rowGap = gap,
  reversed,
  style,
  wrap = 'wrap',
  ...rest
}: HorizontalStackProps<AsComponent>) {
  const Component = (as ?? DEFAULT_HORIZONTAL_STACK_ELEMENT) satisfies ElementType;
  const baseStyle = sanitizeCSSVariables({
    ...style,
    ...(gap ? buildResponsiveProps(COMPONENT_STYLE_NAME, 'gap', gap, 'space') : {}),
    ...buildResponsiveProps(COMPONENT_STYLE_NAME, 'wrap', wrap),
    ...(rowGap ? buildResponsiveProps(COMPONENT_STYLE_NAME, 'row-gap', rowGap, 'space') : {}),
  } as CSSProperties);

  return (
    <Component
      {...rest}
      className={clsx(
        styles.horizontalStack,
        className,
        fullWidth && styles.fullWidth,
        styles[`align-${align}`],
        styles[`block-align-${blockAlign}`],
        reversed && styles.itemsReverse,
      )}
      style={baseStyle}
    >
      {children}
    </Component>
  );
}
